import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Autocomplete, Box, Button, Checkbox, Container, FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, Switch, TextField, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InputFileUpload from '../../../components/events/InputFileUpload';
import ScrollableCheckboxList from '../../../components/events/ScrollableCheckBoxList';
import InfoTooltip from '../../../components/general/InfoTooltip';
import LoadingView from '../../../components/general/LoadingView';
import TimerAlert from '../../../components/general/TimerAlert';
import { selectUser } from '../../../redux/slices/authSlice';
import { fetchClubs, selectClubList } from '../../../redux/slices/clubsSlice';
import { resetCreateEventState, selectCreateEventStatus, setEventData, uploadEvent } from '../../../redux/slices/createEventSlice';
import { fetchPromoters, selectPromoters, selectPromotersStatus } from '../../../redux/slices/promotersSlice';
import { AppDispatch } from '../../../redux/store/store';
import { getGeoCodeFromAdress } from '../../../util/firebaseUtil';
import { Ticket } from '../../../util/types';
import { isTicketPriceConfigurationValid, scrollToTop, ticketRequiresFreeLimit } from '../../../util/utils';


export default function CreateEvent() {
    const dispatch: AppDispatch = useDispatch();
    const clubId = useSelector(selectUser)?.clubId;
    const clubPromoters = useSelector(selectPromoters);
    const clubList = useSelector(selectClubList);
    const reqStatus = useSelector(selectCreateEventStatus);
    const promoterReqStatus = useSelector(selectPromotersStatus);
    const user = useSelector(selectUser);
    const isMaster = user?.userType == "MASTER";
    const [guestListActive, setGuestListActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [validationErrorMsg, setValidationErrorMsg] = useState("");
    const [masterClubId, setMasterClubId] = useState("");
    const [guestListAmount, setGuestListAmount] = useState(100);
    const [promoterProvision, setPromoterProvision] = useState('');
    const [isPublicPromoteChecked, setIsPublicPromoteChecked] = useState(false);
    const [ticketBuyableUntil, setTicketBuyableUntil] = useState("");
    const [checked, setChecked] = useState<string[]>([]);
    const [tickets, setTickets] = useState<Ticket[]>([]);

    const now = new Date();
    const todayAt10PM = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 0, 0);
    const tomorrowAt4AM = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 4, 0, 0);


    const initialEventData = {
        ticketPrice: '',
        endTime: tomorrowAt4AM.toISOString(),
        eventName: '',
        eventLocation: '',
        eventDescription: '',
        eventVenueName: '',
        startTime: todayAt10PM.toISOString(),
        age: '',
        genre: '',
        videoUrl: '',
        paymentLink: ''
    };
    const [localEventData, setLocalEventData] = useState(initialEventData);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLocalEventData({ ...localEventData, [name]: value });
    };

    const handleAddTicket = () => {
        setTickets([
            ...tickets,
            {
                ticketTypeName: '',
                ticketTypePrice: 0,
                ticketsAvailable: 0,
                freeTicketsPerUserLimit: undefined,
                ticketTypeDescription: '',
                ticketBuyableUntil: ''
            }
        ]);
    };

    const handleTicketChange = (index: number, field: keyof Ticket, value: string) => {
        const updatedTickets = [...tickets];
        // Clone the ticket to avoid mutating non-extensible/frozen objects
        const existing = updatedTickets[index] ? { ...updatedTickets[index] } : {} as Ticket;
        const newValue: any = (field === 'ticketTypePrice' || field === 'ticketsAvailable' || field === 'freeTicketsPerUserLimit')
            ? (value === '' ? undefined : Number(value))
            : value;
        (existing as any)[field] = newValue;
        updatedTickets[index] = existing;
        setTickets(updatedTickets);
    };

    const handleRemoveTicket = (index: number) => {
        const updatedTickets = tickets.filter((_, i) => i !== index);
        setTickets(updatedTickets);
    };

    const handleTicketDateChange = (index: number, date: Dayjs | null) => {
        const updatedTickets = [...tickets];
        const existing = updatedTickets[index] ? { ...updatedTickets[index] } : {} as Ticket;
        existing.ticketBuyableUntil = date ? date.toISOString() : '';
        updatedTickets[index] = existing;
        setTickets(updatedTickets);
    };



    const handleDateTimeChange = (date: Dayjs | null, fieldName: string) => {
        if (!date) return;

        const dateString = date.toISOString();
        let newEventData = { ...localEventData, [fieldName]: dateString };

        if (fieldName === 'startTime') {
            // Set endTime to one day after startTime at 4 AM
            const endTime = date.add(1, 'day').hour(4).minute(0).second(0);
            newEventData = {
                ...newEventData,
                endTime: endTime.toISOString(),
            };
        }

        setLocalEventData(newEventData);
    };

    async function handleSubmit() {
        setValidationErrorMsg("");
        if (await validateInputsAndShowError()) {
            const { lat, lng } = await getGeoCodeFromAdress(localEventData.eventLocation);
            if (lat && lng) {
                dispatch(uploadEvent({
                    eventData: {
                        ...localEventData,
                        clubId: masterClubId ? masterClubId : clubId ?? "",
                        eventLocation: { latitude: lat, longitude: lng },
                        eventAddress: localEventData.eventLocation,
                        tickets: tickets,
                        promoters: checked,
                        promoterProvision: guestListActive ? parseInt(promoterProvision) : 0,
                        publicPromote: guestListActive ? isPublicPromoteChecked : false
                    },
                    file: selectedFile
                }));
                dispatch(setEventData(localEventData));
                resetPage();
            } else {
                setValidationErrorMsg("Die angegebene Adresse ist falsch!")
            }
        }
    };

    function resetPage() {
        setLocalEventData(initialEventData)
        setSelectedFile(null);
        setGuestListActive(false);
        setMasterClubId("");
        setGuestListAmount(100);
        scrollToTop();
    }

    async function validateInputsAndShowError() {
        const chain = localEventData.startTime &&
            localEventData.endTime &&
            localEventData.eventName &&
            localEventData.eventDescription &&
            localEventData.eventLocation &&
            selectedFile &&
            (!guestListActive || (promoterProvision !== undefined && promoterProvision !== '')) &&
            (!guestListActive || (ticketBuyableUntil !== undefined && ticketBuyableUntil !== '')) &&
            tickets.every(ticket =>
                ticket.ticketTypeName &&
                isTicketPriceConfigurationValid(ticket) &&
                (ticket.ticketsAvailable !== undefined && ticket.ticketsAvailable >= 0)
            );



        if (!chain) {
            const errorList: string[] = [];
            if (!localEventData.startTime) {
                errorList.push("Startzeit");
            }
            if (!localEventData.endTime) {
                errorList.push("Endzeit");
            }
            if (!localEventData.eventName) {
                errorList.push("Event Name");
            }
            if (!localEventData.eventDescription) {
                errorList.push("Beschreibung");
            }
            if (!localEventData.eventLocation) {
                errorList.push("Event Adresse");
            }
            if (!selectedFile) {
                errorList.push("Bilddatei");
            }
            if (guestListActive && (promoterProvision === undefined || promoterProvision === '')) {
                errorList.push("Provison pro CheckIn in EUR");
            }
            if (guestListActive && (ticketBuyableUntil === undefined || ticketBuyableUntil === '')) {
                errorList.push("Gästeliste Gültigkeit");
            }
            if (tickets.some(ticket =>
                !ticket.ticketTypeName ||
                !isTicketPriceConfigurationValid(ticket) ||
                ticket.ticketsAvailable === undefined ||
                Number.isNaN(ticket.ticketsAvailable) ||
                ticket.ticketsAvailable < 0)) {
                errorList.push("Alle Ticketfelder müssen ausgefüllt sein. Kostenlose normale Tickets brauchen ein Limit von mindestens 1 pro User, bezahlte Tickets einen Preis größer als 0 (Menge: 0 oder größer).");
            }
            if (errorList.length > 0) {
                const errorMessage = `Die folgenden Felder sind nicht ausgefüllt, werden jedoch benötigt: ${errorList.join(', ')}`;
                setValidationErrorMsg(errorMessage);
                console.error(errorMessage);
            }
        }

        return chain;
    }
    useEffect(() => {
        async function fetchData() {
            dispatch(fetchPromoters())
            if (user?.userType === "MASTER") {
                dispatch(fetchClubs())
            }
        }
        fetchData();
        return () => {
            dispatch(resetCreateEventState());
        };
    }, [])

    if (reqStatus === "pending" || promoterReqStatus === "pending") {
        return <LoadingView />
    }


    return (
        <Container maxWidth="md" sx={{ marginTop: 10, marginBottom: 10 }}>
            {validationErrorMsg && <Alert severity="error">{validationErrorMsg}</Alert>}
            {reqStatus === "succeeded" && <TimerAlert variant={"success"} children={"Event erfolgreich erstellt. Achtung es dauert bis zu 3h bis dein Event sichtbar ist"} />}
            <Typography variant="h4" gutterBottom>
                Add Event
            </Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputFileUpload selectedFile={selectedFile} setSelectedFile={setSelectedFile} />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label="Start"
                            ampm={false}
                            name="startTime"
                            value={localEventData.startTime ? dayjs(localEventData.startTime) : null}
                            onChange={(date) => handleDateTimeChange(date, 'startTime')}
                        //renderInput={(props) => <TextField {...props} />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label="Ende"
                            ampm={false}
                            name="endTime"
                            value={localEventData.endTime ? dayjs(localEventData.endTime) : null}
                            onChange={(date) => handleDateTimeChange(date, 'endTime')}
                        //renderInput={(props) => <TextField {...props} />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            required
                            label="Event Name"
                            name="eventName"
                            value={localEventData.eventName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Mindestalter"
                            name="age"
                            value={localEventData.age}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            required
                            label="Adresse"
                            name="eventLocation"
                            value={localEventData.eventLocation}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Name des Veranstaltungsortes"
                            name="eventVenueName"
                            value={localEventData.eventVenueName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Genre"
                            name="genre"
                            value={localEventData.genre}
                            onChange={handleChange}
                            helperText="z.B. HIP-HOP, TECHNO usw..."
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Typography variant="h6" gutterBottom sx={{ m: 0 }}>
                                Tickets
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddTicket}
                            >
                                Ticket hinzufügen
                            </Button>
                        </Box>
                    </Grid>

                    {tickets.map((ticket, index) => (
                        <Fragment key={index}>
                            <Grid item xs={3}>
                                <TextField
                                    label="Ticketname"
                                    value={ticket.ticketTypeName}
                                    onChange={(e) => handleTicketChange(index, 'ticketTypeName', e.target.value)}
                                    fullWidth
                                    required
                                    helperText="Name des Tickets"
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Ticketpreis"
                                    type="number"
                                    value={ticket.ticketTypePrice}
                                    onChange={(e) => handleTicketChange(index, 'ticketTypePrice', e.target.value)}
                                    fullWidth
                                    required
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                    }}
                                    helperText="Preis dieses Tickets"
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Verfügbare Tickets"
                                    type="number"
                                    value={ticket.ticketsAvailable}
                                    onChange={(e) => handleTicketChange(index, 'ticketsAvailable', e.target.value)}
                                    fullWidth
                                    required
                                    helperText="Anzahl verfügbarer Tickets"
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Ticketbeschreibung"
                                    value={ticket.ticketTypeDescription}
                                    onChange={(e) => handleTicketChange(index, 'ticketTypeDescription', e.target.value)}
                                    fullWidth
                                    required
                                    helperText="Kurze Beschreibung für das Ticket"
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <DateTimePicker
                                    label="Verkauf bis"
                                    ampm={false}
                                    value={ticket.ticketBuyableUntil ? dayjs(ticket.ticketBuyableUntil) : null}
                                    onChange={(date) => handleTicketDateChange(index, date)}
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                                <IconButton aria-label="Ticket entfernen" color="error" onClick={() => handleRemoveTicket(index)}>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Grid>
                            {ticketRequiresFreeLimit(ticket) && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Kostenlose Tickets pro User"
                                        type="number"
                                        value={ticket.freeTicketsPerUserLimit ?? ''}
                                        onChange={(e) => handleTicketChange(index, 'freeTicketsPerUserLimit', e.target.value)}
                                        fullWidth
                                        required
                                        inputProps={{ min: 1, step: 1 }}
                                        helperText="Pflichtfeld für normale kostenlose Tickets."
                                    />
                                </Grid>
                            )}

                        </Fragment>
                    ))}

                    {tickets.length < 1 ? (
                        <Grid item xs={12}>
                            <Alert severity="info">Noch keine Tickets hinzugefügt. Klicke oben auf "Ticket hinzufügen".</Alert>
                        </Grid>
                    ) : null}

                    {/* Placeholder for design */}
                    {isMaster && <Grid item xs={6}>
                    </Grid>}


                    {isMaster && (
                        <Grid item xs={6}>
                            <Autocomplete
                                options={clubList || []}
                                getOptionLabel={(option) => option.name || ''}
                                value={clubList?.find(club => club.clubId === masterClubId) || null}
                                onChange={(event, newValue) => {
                                    setMasterClubId(newValue?.clubId || '');
                                    var club = clubList?.find(club => club.clubId === newValue?.clubId);
                                    const clubAdress = club?.address.street + ", " + club?.address.city
                                        + " " + club?.address.plz;
                                    setLocalEventData({ ...localEventData, "eventLocation": clubAdress });
                                }}
                                renderInput={(params) => <TextField {...params} label="Clubs" variant="outlined" fullWidth />}
                            />
                        </Grid>
                    )}
                    {isMaster && <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="VideoUrl"
                            name="videoUrl"
                            value={localEventData.videoUrl}
                            onChange={handleChange}
                        />
                    </Grid>}
                    {isMaster && <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Payment Link"
                            name="paymentLink"
                            value={(localEventData as any).paymentLink}
                            onChange={handleChange}
                        />
                    </Grid>}

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="Beschreibung"
                            name="eventDescription"
                            value={localEventData.eventDescription}
                            multiline
                            rows={4}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box onClick={() => alert("Schreib uns um das Gästelisten Feature aktivieren zu lassen!")} >
                            <FormGroup>
                                <FormControlLabel
                                    control={<Switch checked={guestListActive} onChange={() => setGuestListActive(!guestListActive)} disabled />}
                                    label="Gästeliste" />
                            </FormGroup>
                        </Box>
                    </Grid>
                    {guestListActive && <Grid item xs={4}>
                        <TextField
                            fullWidth
                            label="Anzahl Gästeliste"
                            name="guestListAmount"
                            value={guestListAmount}
                            onChange={(e) => setGuestListAmount(parseInt(e.target.value))}
                        />
                    </Grid>}
                    {guestListActive && <Grid item xs={6}>
                        <DateTimePicker
                            label="Gültig bis"
                            ampm={false}
                            name="ticketBuyableUntil"
                            value={ticketBuyableUntil ? dayjs(ticketBuyableUntil) : null}
                            onChange={(date) => setTicketBuyableUntil(date && date.isValid() ? date.toDate().toISOString() : "")}
                        />
                    </Grid>}
                    {guestListActive && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isPublicPromoteChecked}
                                        onChange={(e) => setIsPublicPromoteChecked(e.target.checked)}
                                    />
                                }
                                label={
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        Public Joinable
                                        <InfoTooltip title="Dieses Event ist öffentlich zugänglich für alle Promoter.
                                         D.h. alle Promoter können dein Event promoten und werden im Falle eines Check Ins bezahlt." />
                                    </div>
                                }
                            />
                        </Grid>
                    )}
                    {guestListActive && <Grid item xs={12}>
                        <ScrollableCheckboxList
                            clubPromoters={clubPromoters ?? []}
                            setChecked={setChecked}
                            checked={checked} />
                    </Grid>}
                    {guestListActive && (
                        <Grid item xs={12}>
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16 }}>
                                <TextField
                                    value={promoterProvision}
                                    required
                                    label="Provison pro CheckIn in EUR"
                                    variant="outlined"
                                    sx={{ width: "50%" }}
                                    margin="normal"
                                    onChange={(e) => setPromoterProvision(e.target.value)}
                                />
                                <InfoTooltip title="Dies ist die Provision für den Promoter angegeben in EUR. 
                                    Der Promoter erhält nur Geld wenn ein Gast sich am Abend wirklich auch eincheckt." />
                            </div>
                        </Grid>
                    )}
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ background: "#ef5350" }}
                            onClick={resetPage}
                        >
                            Clear
                        </Button>
                    </Grid>
                    <Grid item xs={8}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            Add
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}
